import _ from 'lodash';

import * as GuestProfileActions from 'actions/GuestProfileActions';
import { AutoFocusType } from 'components/booking-modal/BookingProfileFormTab';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { dashboard } from 'types';
import { findNoteWithNoteType } from 'utils/noteUtils';
import { getAllPatronStats } from 'actions/PatronStatsActions';
import {
  getChecksByPartyId,
  getChecksByPurchase,
  getCurrentPurchaseById,
  getPurchaseTimelineEvents,
  getTicketGroup,
  getVisitForWalkIn,
} from 'actions/BookingActions';
import { getRelevantAuditedNotesFromProfile } from 'utils/guestProfileUtils';
import { trimmedStringsAreDifferent } from 'utils/stringUtils';
import protos from 'utils/protos';

const { BookingNote } = protos;

export function getInitialData(
  patronId: number,
  ticketGroupId: number,
  businessId: number,
  purchaseId: number,
  partyId?: number,
  serviceDate?: string
): ApiAction {
  return (dispatch: Dispatch) => {
    // FIXME: The server rejects this message if it's part of a batch.

    dispatch(getGuest(patronId));
    dispatch(
      getPurchase({
        ticketGroupId,
        partyId,
        serviceDate,
        patronId,
        purchaseId,
      })
    );
    dispatch(getAllPatronStats(patronId, businessId));
  };
}

export function getDataForPurchaseAndPatron(purchaseId: number, patronId: number): ApiAction {
  return (dispatch: Dispatch) => {
    dispatch(getDataForPurchase(purchaseId));
    return dispatch(getDataForGuest(patronId));
  };
}

export function getDataForPurchase(purchaseId: number): ApiAction {
  return (dispatch: Dispatch) => {
    dispatch(getCurrentPurchaseById(purchaseId));
    dispatch(getChecksByPurchase(purchaseId));
    return dispatch(getPurchaseTimelineEvents(purchaseId));
  };
}

export function getDataForGuest(patronId: number): ApiAction {
  return (dispatch: Dispatch) => {
    dispatch(getGuest(patronId));
    return dispatch(GuestProfileActions.fetchAdminPurchaseHistory(patronId));
  };
}

export function getHeldTableInitialData(
  patronId: number,
  ticketGroupId: number,
  businessId: number
): ApiAction {
  return (dispatch: Dispatch) => {
    if (patronId) {
      dispatch(getGuest(patronId));
    }
    dispatch(
      getPurchase({
        ticketGroupId,
        patronId,
      })
    );

    // FIXME: The server rejects this message if it's part of a batch.
    if (patronId && businessId) {
      dispatch(getAllPatronStats(patronId, businessId));
    }
  };
}

export function getAssociatedWalkInData(
  patronId: number,
  businessId: number,
  partyId: number,
  serviceDate: string | undefined
) {
  return (dispatch: Dispatch) => {
    dispatch(getVisitForWalkIn(partyId, serviceDate));
    dispatch(getDataForGuest(patronId));
    dispatch(getAllPatronStats(patronId, businessId));
  };
}

export function getGuest(patronId: number): ApiAction | null {
  if (!patronId) {
    return null;
  }
  return GuestProfileActions.get(patronId);
}

// TODO: Refactor / rename to do what it says it does and not a bunch more
export function getPurchase(options: {
  ticketGroupId: number;
  partyId?: number;
  serviceDate?: string;
  patronId?: number;
  purchaseId?: number;
}) {
  const { ticketGroupId, partyId, serviceDate, patronId, purchaseId } = options;
  // The walk in will be present on the ticket group. If there isn't one (a walk in),
  // query the visit seperately.
  return (dispatch: Dispatch) => {
    if (ticketGroupId) {
      dispatch(getTicketGroup(ticketGroupId));
    } else if (partyId && isWalkinWithoutPurchase(partyId, purchaseId) && serviceDate) {
      dispatch(getVisitForWalkIn(partyId, serviceDate));
      dispatch(getChecksByPartyId(partyId, serviceDate));
    }
    if (purchaseId) {
      dispatch(getCurrentPurchaseById(purchaseId));
    }

    if (purchaseId) {
      dispatch(getChecksByPurchase(purchaseId));
    }
    if (patronId) {
      dispatch(GuestProfileActions.fetchAdminPurchaseHistory(patronId));
    }
  };
}

function isWalkinWithoutPurchase(partyId: number, purchaseId?: number) {
  return partyId < 0 && !purchaseId;
}

export function getTimeline(purchaseId: number): ApiAction | BaseTockAction {
  if (purchaseId) {
    return getPurchaseTimelineEvents(purchaseId);
  }
  return {
    type: SharedAppConstants.NULL,
  };
}

export function clearState(): BaseTockAction {
  return {
    type: SharedAppConstants.BOOKING_MODAL_CLEAR,
  };
}

export function setEditing(editing: boolean, autoFocus?: AutoFocusType): BaseTockAction {
  return {
    type: SharedAppConstants.BOOKING_MODAL_SET_EDITING,
    payload: {
      editing,
      autoFocus,
    },
  };
}

export interface NoteObject {
  note?: string;
  noteType: 'GENERAL' | 'DIETARY';
}

interface TicketGroupBookingNotes {
  type: 'TICKET_GROUP';
  bookingNote: NoteObject;
  auditedBookingNotes?: dashboard.AuditedNote[] | dashboard.IAuditedNote[];
  ticketGroupId: number;
}

interface TicketSubsetBookingNotes {
  type: 'TICKET_SUBSET';
  bookingNote: NoteObject;
  auditedBookingNotes?: dashboard.AuditedNote[] | dashboard.IAuditedNote[];
  ticketSubsetId: number;
}

interface PurchaseBookingNotes {
  type: 'PURCHASE';
  bookingNote: NoteObject;
  auditedBookingNotes?: dashboard.AuditedNote[] | dashboard.IAuditedNote[];
  purchaseId: number;
}

type BookingNotesUpdate = TicketGroupBookingNotes | TicketSubsetBookingNotes | PurchaseBookingNotes;

export function updateBookingNotes(notesUpdate: BookingNotesUpdate): ApiAction {
  const formBookingNote = notesUpdate.bookingNote;
  const relevantAuditedBookingNote = findNoteWithNoteType(
    formBookingNote.noteType,
    notesUpdate.auditedBookingNotes
  );

  let purchaseId;
  let ticketGroupId;
  let ticketSubsetId;
  switch (notesUpdate.type) {
    case 'TICKET_GROUP':
      ticketGroupId = notesUpdate.ticketGroupId;
      break;
    case 'PURCHASE':
      purchaseId = notesUpdate.purchaseId;
      break;
    case 'TICKET_SUBSET':
      ticketSubsetId = notesUpdate.ticketSubsetId;
      break;
    default:
      assertNever(notesUpdate);
  }

  const previousVersionId = relevantAuditedBookingNote?.previousVersionId;

  const proto = BookingNote.fromObject({
    purchaseId,
    ticketGroupId,
    note: _.trim(formBookingNote.note),
    noteType: formBookingNote.noteType,
    previousVersionId,
    ticketSubsetId,
  });

  return {
    TockApi: {
      postProto: '/note/ticket/group',
      type: SharedAppConstants.PURCHASE_BOOKING_NOTE_UPDATE,
      request: proto,
      toast: 'Note updated',
      // scope, TODO pipe this through
    },
  };
}

export function updatePatronNotes(
  patronNotes: NoteWithNoteTypeAndScope[],
  profile: dashboard.GuestProfile,
  patronId: number,
  businessId: number
): ApiAction {
  return (dispatch: Dispatch) => {
    _.forEach(patronNotes, (formPatronNote) => {
      const auditedNotes = getRelevantAuditedNotesFromProfile(formPatronNote.noteScope, profile);
      const relevantAuditedNote = findNoteWithNoteType(formPatronNote.noteType, auditedNotes);
      if (!trimmedStringsAreDifferent(formPatronNote.note, relevantAuditedNote?.note)) {
        return;
      }
      const previousVersionId = relevantAuditedNote?.previousVersionId;
      dispatch(
        GuestProfileActions.setNote(
          patronId,
          businessId,
          _.trim(formPatronNote.note),
          formPatronNote.noteType as dashboard.NoteType,
          formPatronNote.noteScope as dashboard.NoteScope,
          previousVersionId || undefined
        )
      );
    });
  };
}

export function updatePatronTags(patronTags: string[], existingTags: string[], patronId: number) {
  return (dispatch: Dispatch) => {
    const tagsToAdd = _.difference(patronTags, existingTags);
    const tagsToRemove = _.difference(existingTags, patronTags);
    if (_.isEmpty(tagsToAdd) && _.isEmpty(tagsToRemove)) {
      return;
    }
    dispatch(GuestProfileActions.updatePatronTags(patronId, tagsToAdd, tagsToRemove));
  };
}
